package com.shadowings.gero.data.repository

import com.shadowings.gero.domain.model.SavedBuilderWord
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.domain.repository.AuthRepository
import com.shadowings.gero.domain.repository.CloudSyncRepository
import com.shadowings.gero.domain.repository.UserDataRepository
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.firestoreSettings
import dev.gitlive.firebase.firestore.persistentCacheSettings
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch

class CloudSyncRepositoryImpl(
    private val authRepository: AuthRepository,
    private val userDataRepository: UserDataRepository,
) : CloudSyncRepository {

    private val db = Firebase.firestore

    init {
        db.settings = firestoreSettings {
            cacheSettings = persistentCacheSettings {}
        }
    }

    private val _isSyncing = MutableStateFlow(false)
    override val isSyncing = _isSyncing

    private val scope = CoroutineScope(Dispatchers.Default)
    private var syncJob: Job? = null
    private var isResetting = false

    override fun startAutoSync() {
        scope.launch {
            authRepository.currentUser.collect { user ->
                syncJob?.cancel()
                if (user != null) {
                    val userId = user.uid
                    syncJob = launch {
                        val docRef = db.collection("users").document(userId)

                        // 1. Bootstrap on login: merge cloud and local, update both local and cloud.
                        try {
                            _isSyncing.value = true
                            val snapshot = try { docRef.get() } catch (e: Exception) { null }
                            val localData = userDataRepository.userData.first()
                            if (snapshot != null && snapshot.exists) {
                                val cloudData = snapshot.data<UserData>()
                                val merged = mergeUserData(localData, cloudData)
                                if (merged != localData) {
                                    userDataRepository.restoreUserData(merged)
                                    com.shadowings.gero.util.requestUiRepaint()
                                }
                                if (merged != cloudData) {
                                    docRef.set(merged)
                                }
                            } else {
                                docRef.set(localData)
                            }
                        } catch (e: Exception) {
                            e.printStackTrace()
                        } finally {
                            _isSyncing.value = false
                        }

                        // 2. Snapshot Listener from Cloud to Local (Multi-device Sync)
                        launch {
                            try {
                                docRef.snapshots.collect { snapshot ->
                                    if (isResetting) return@collect
                                    if (snapshot.exists) {
                                        val cloudData = snapshot.data<UserData>()
                                        val currentLocal = userDataRepository.userData.first()
                                        val merged = mergeUserData(currentLocal, cloudData)
                                        // Update local only if it's different to prevent infinite cycles
                                        if (merged != currentLocal) {
                                            try {
                                                _isSyncing.value = true
                                                userDataRepository.restoreUserData(merged)
                                                com.shadowings.gero.util.requestUiRepaint()
                                            } finally {
                                                _isSyncing.value = false
                                            }
                                        }
                                    }
                                }
                            } catch (e: Exception) {
                                e.printStackTrace()
                            }
                        }

                        // 3. Local changes to Cloud
                        launch {
                            try {
                                userDataRepository.userData.collect { localData ->
                                    if (isResetting) return@collect
                                    val snapshot = try { docRef.get() } catch (_: Exception) { null }
                                    val cloudData = if (snapshot != null && snapshot.exists) {
                                        snapshot.data<UserData>()
                                    } else {
                                        null
                                    }
                                    val merged = if (cloudData != null) mergeUserData(localData, cloudData) else localData

                                    // Upload only if different from cloud or if it's a new user document
                                    if (cloudData == null || merged != cloudData) {
                                        try {
                                            _isSyncing.value = true
                                            docRef.set(merged)
                                        } finally {
                                            _isSyncing.value = false
                                        }
                                    }
                                }
                            } catch (e: Exception) {
                                e.printStackTrace()
                            }
                        }
                    }
                }
            }
        }
    }

    override suspend fun resetCloudData(userId: String): Result<Unit> {
        return try {
            _isSyncing.value = true
            val docRef = db.collection("users").document(userId)
            val resetData = UserData()
            docRef.set(resetData)
            _isSyncing.value = false
            Result.success(Unit)
        } catch (e: Exception) {
            _isSyncing.value = false
            Result.failure(e)
        }
    }

    override suspend fun resetAllData(userId: String?): Result<Unit> {
        return try {
            isResetting = true
            _isSyncing.value = true
            userDataRepository.resetData()
            if (userId != null) {
                val docRef = db.collection("users").document(userId)
                val resetData = UserData()
                docRef.set(resetData)
            }
            _isSyncing.value = false
            Result.success(Unit)
        } catch (e: Exception) {
            _isSyncing.value = false
            Result.failure(e)
        } finally {
            isResetting = false
        }
    }
}

// consumablesUpdatedAt/lastModified are String (not Long) to avoid Kotlin/JS's Long
// representation breaking Firestore writes — parse back to Long for numeric comparison here.
private fun String.toEpochMillisOrZero(): Long = toLongOrNull() ?: 0L

internal fun mergeUserData(local: UserData, cloud: UserData): UserData {
    val preferLocal = local.lastModified.toEpochMillisOrZero() >= cloud.lastModified.toEpochMillisOrZero()
    val mergedQuizDates = (local.completedQuizDates + cloud.completedQuizDates)
        .filter { it.isNotBlank() }
        .distinct()
        .sorted()
    val mergedStrike = if (mergedQuizDates.isNotEmpty()) {
        com.shadowings.gero.util.calculateStreak(mergedQuizDates)
    } else {
        when {
            local.lastQuizDate > cloud.lastQuizDate -> local.strike
            cloud.lastQuizDate > local.lastQuizDate -> cloud.strike
            else -> maxOf(local.strike, cloud.strike)
        }
    }
    val mergedLastQuizDate = if (mergedQuizDates.isNotEmpty()) {
        com.shadowings.gero.util.dateStringToDateInt(mergedQuizDates.last())
    } else {
        maxOf(local.lastQuizDate, cloud.lastQuizDate)
    }

    val localConsumablesUpdatedAt = local.consumablesUpdatedAt.toEpochMillisOrZero()
    val cloudConsumablesUpdatedAt = cloud.consumablesUpdatedAt.toEpochMillisOrZero()
    val mergedHearts = when {
        localConsumablesUpdatedAt > cloudConsumablesUpdatedAt -> local.hearts
        cloudConsumablesUpdatedAt > localConsumablesUpdatedAt -> cloud.hearts
        else -> maxOf(local.hearts, cloud.hearts)
    }

    val localWrongAnswersUpdatedAt = local.wrongAnswersUpdatedAt.toEpochMillisOrZero()
    val cloudWrongAnswersUpdatedAt = cloud.wrongAnswersUpdatedAt.toEpochMillisOrZero()
    val preferLocalWrongAnswers = localWrongAnswersUpdatedAt >= cloudWrongAnswersUpdatedAt

    val localFavoriteHierosUpdatedAt = local.favoriteHierosUpdatedAt.toEpochMillisOrZero()
    val cloudFavoriteHierosUpdatedAt = cloud.favoriteHierosUpdatedAt.toEpochMillisOrZero()
    val localFavoriteWordsUpdatedAt = local.favoriteWordsUpdatedAt.toEpochMillisOrZero()
    val cloudFavoriteWordsUpdatedAt = cloud.favoriteWordsUpdatedAt.toEpochMillisOrZero()

    return UserData(
        stars = mergeMaxMap(local.stars, cloud.stars),
        perfect = local.perfect + cloud.perfect,
        explanations = local.explanations + cloud.explanations,
        experience = maxOf(local.experience, cloud.experience),
        strike = mergedStrike,
        completedQuizDates = mergedQuizDates,
        tutorialDone = chooseMaxTutorialDone(local.tutorialDone, cloud.tutorialDone),
        alynColors = mergeColorsByTimestamp(local.alynColors, cloud.alynColors, preferLocal),
        niloColors = mergeColorsByTimestamp(local.niloColors, cloud.niloColors, preferLocal),
        zetaColors = mergeColorsByTimestamp(local.zetaColors, cloud.zetaColors, preferLocal),
        maskoColors = mergeColorsByTimestamp(local.maskoColors, cloud.maskoColors, preferLocal),
        chapter = maxOf(local.chapter, cloud.chapter),
        hearts = mergedHearts,
        lastOpenDate = maxOf(local.lastOpenDate, cloud.lastOpenDate),
        lastQuizDate = mergedLastQuizDate,
        // Union would resurrect a favorite removed on one side once it merges against the other
        // side's stale copy (same bug as savedBuilderWords), so the whole set comes from whichever
        // side touched *that set* more recently. Gated on its own timestamp, not the shared
        // lastModified, for the same reason as wrongAnswers below: a fresh install bumps
        // lastModified during onboarding, and its empty favorites would otherwise wipe the cloud's.
        favoriteHieros = mergeFavoritesByTimestamp(
            local.favoriteHieros, localFavoriteHierosUpdatedAt,
            cloud.favoriteHieros, cloudFavoriteHierosUpdatedAt,
            preferLocal
        ),
        favoriteWords = mergeFavoritesByTimestamp(
            local.favoriteWords, localFavoriteWordsUpdatedAt,
            cloud.favoriteWords, cloudFavoriteWordsUpdatedAt,
            preferLocal
        ),
        language = if (preferLocal) local.language.ifBlank { cloud.language } else cloud.language.ifBlank { local.language },
        consumablesUpdatedAt = maxOf(localConsumablesUpdatedAt, cloudConsumablesUpdatedAt).toString(),
        walkthroughDone = local.walkthroughDone || cloud.walkthroughDone,
        compendioWalkthroughDone = local.compendioWalkthroughDone || cloud.compendioWalkthroughDone,
        builderWalkthroughDone = local.builderWalkthroughDone || cloud.builderWalkthroughDone,
        charactersWalkthroughDone = local.charactersWalkthroughDone || cloud.charactersWalkthroughDone,
        profileWalkthroughDone = local.profileWalkthroughDone || cloud.profileWalkthroughDone,
        lastSeenChangelogVersion = if (preferLocal) local.lastSeenChangelogVersion.ifBlank { cloud.lastSeenChangelogVersion } else cloud.lastSeenChangelogVersion.ifBlank { local.lastSeenChangelogVersion },
        lastModified = maxOf(local.lastModified.toEpochMillisOrZero(), cloud.lastModified.toEpochMillisOrZero()).toString(),
        savedBuilderWords = mergeSavedBuilderWordsByTimestamp(local.savedBuilderWords, cloud.savedBuilderWords, preferLocal),
        reviewPromptDone = local.reviewPromptDone || cloud.reviewPromptDone,
        isFullVersionUnlocked = local.isFullVersionUnlocked || cloud.isFullVersionUnlocked,
        streakRestorationTokens = mergeStreakRestorationTokens(
            local.streakRestorationTokens,
            cloud.streakRestorationTokens
        ),
        streakRestorationBackfillDone = local.streakRestorationBackfillDone || cloud.streakRestorationBackfillDone,
        streakRestorationBadgeSeen = mergeStreakRestorationBadgeSeen(local, cloud),
        answeredFeaturePolls = mergeAnsweredFeaturePolls(
            local.answeredFeaturePolls,
            cloud.answeredFeaturePolls
        ),
        // Grows without a natural bound (unlike savedBuilderWords), so — like
        // favoriteHieros/favoriteWords above — the whole list comes from whichever side
        // was modified more recently, instead of a per-id union+tombstone that would make it (and
        // the synced document) grow forever. Gated on its own wrongAnswersUpdatedAt rather than the
        // shared preferLocal/lastModified: lastModified is bumped by any unrelated local edit
        // (language, theme, tutorial flags, ...), so on a freshly logged-in device that had touched
        // anything else locally before syncing, preferLocal could be true while local.wrongAnswers
        // was still empty — silently wiping the cloud's real list on write-back.
        wrongAnswers = if (preferLocalWrongAnswers) local.wrongAnswers else cloud.wrongAnswers,
        wrongAnswersUpdatedAt = maxOf(localWrongAnswersUpdatedAt, cloudWrongAnswersUpdatedAt).toString(),
        favoriteHierosUpdatedAt = maxOf(localFavoriteHierosUpdatedAt, cloudFavoriteHierosUpdatedAt).toString(),
        favoriteWordsUpdatedAt = maxOf(localFavoriteWordsUpdatedAt, cloudFavoriteWordsUpdatedAt).toString(),
    )
}

// Not a plain OR: granting a new token resets the flag to false on that device, and OR would let
// the other side's stale "seen" (acknowledged before that grant existed) hide the new badge
// forever. A "seen" side only overrides an "unseen" one if it already knew every token level the
// unseen side has, i.e. the user dismissed the badge after those tokens were granted.
private fun mergeStreakRestorationBadgeSeen(local: UserData, cloud: UserData): Boolean {
    if (local.streakRestorationBadgeSeen == cloud.streakRestorationBadgeSeen) {
        return local.streakRestorationBadgeSeen
    }
    val (seen, unseen) = if (local.streakRestorationBadgeSeen) local to cloud else cloud to local
    return seen.streakRestorationTokens.keys.containsAll(unseen.streakRestorationTokens.keys)
}

// Same union-of-keys + "true wins" rule as mergeStreakRestorationTokens below, applied to
// poll-answered flags instead of token grants.
private fun mergeAnsweredFeaturePolls(
    local: Map<String, Boolean>,
    cloud: Map<String, Boolean>
): Map<String, Boolean> {
    val allKeys = local.keys + cloud.keys
    return allKeys.associateWith { key ->
        (local[key] ?: false) || (cloud[key] ?: false)
    }
}

// Union of keys (each level's token is the same slot on both sides, so unioning never
// double-grants it), and "used" (true) always wins per key over "unused" (false) — the same
// once-only-boolean merge rule as reviewPromptDone/isFullVersionUnlocked above, applied per level
// instead of to the whole field. This is what makes the merge idempotent regardless of how many
// times either device granted or redeemed the same level's token before syncing.
private fun mergeStreakRestorationTokens(
    local: Map<String, Boolean>,
    cloud: Map<String, Boolean>
): Map<String, Boolean> {
    val allKeys = local.keys + cloud.keys
    return allKeys.associateWith { key ->
        (local[key] ?: false) || (cloud[key] ?: false)
    }
}

internal fun mergeUserDataForLogin(local: UserData, cloud: UserData): UserData = mergeUserData(local, cloud)

// Newer per-set timestamp wins outright. On a tie (typically "0" vs "0": data written before these
// timestamps existed, or a fresh install that never touched favorites) fall back to preferLocal, but
// never let an empty set beat a non-empty one — same rule as mergeColorsByTimestamp.
private fun mergeFavoritesByTimestamp(
    local: Set<String>,
    localUpdatedAt: Long,
    cloud: Set<String>,
    cloudUpdatedAt: Long,
    preferLocal: Boolean
): Set<String> = when {
    localUpdatedAt > cloudUpdatedAt -> local
    cloudUpdatedAt > localUpdatedAt -> cloud
    preferLocal -> local.ifEmpty { cloud }
    else -> cloud.ifEmpty { local }
}

private fun mergeColorsByTimestamp(
    localColors: Map<String, Int>,
    cloudColors: Map<String, Int>,
    preferLocal: Boolean
): Map<String, Int> {
    val (primary, secondary) = if (preferLocal) localColors to cloudColors else cloudColors to localColors
    return primary.ifEmpty { secondary }
}

// Per-id merge keyed by each word's own `timestamp`, so a delete (tombstoned with a fresh
// timestamp, see UserDataRepositoryImpl.deleteBuilderWord) always beats a stale copy of the same
// id from the other side instead of a plain union resurrecting it. Ties fall back to `preferLocal`.
// Tombstones are kept in the result on purpose — dropping them here would lose the delete marker
// before a slower device gets a chance to see it. Callers that display the list must filter
// `!deleted` themselves.
internal fun mergeSavedBuilderWordsByTimestamp(
    local: List<SavedBuilderWord>,
    cloud: List<SavedBuilderWord>,
    preferLocal: Boolean
): List<SavedBuilderWord> {
    val (primary, secondary) = if (preferLocal) local to cloud else cloud to local
    val byId = LinkedHashMap<String, SavedBuilderWord>()
    for (word in primary + secondary) {
        val existing = byId[word.id]
        if (existing == null || word.timestamp.toEpochMillisOrZero() > existing.timestamp.toEpochMillisOrZero()) {
            byId[word.id] = word
        }
    }
    return byId.values.toList()
}

private fun <K> mergeMaxMap(local: Map<K, Int>, cloud: Map<K, Int>): Map<K, Int> {
    val allKeys = local.keys + cloud.keys
    return allKeys.associateWith { key ->
        maxOf(local[key] ?: 0, cloud[key] ?: 0)
    }
}

private fun chooseMaxTutorialDone(local: Boolean?, cloud: Boolean?): Boolean? {
    return when {
        local == true || cloud == true -> true
        local == false || cloud == false -> false
        else -> null
    }
}
